import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import axiosInstance from "../../axiosInstance";
import PublicLayout from "../components/PublicLayout";

const fallbackContent = {
  title: "Contact Us",
  description: "<p>Please use the form below and our team will get back to you.</p>",
};

const PublicContactPage = () => {
  const [page, setPage] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [pageResponse, homeResponse] = await Promise.all([
          axiosInstance.get("/pages/public/contact-us"),
          axiosInstance.get("/home-data"),
        ]);
        setPage(pageResponse.data?.page || null);
        setHomeData(homeResponse.data?.homeData || null);
      } catch (error) {
        setPage(null);
        setHomeData(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const content = useMemo(() => page || fallbackContent, [page]);

  const submitContact = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      await axiosInstance.post("/contact-enquire/", formData);
      toast.success("Message sent successfully");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Unable to send message right now");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>{content.metaTitle || `${content.title} | YNB Jobs`}</title>
        <meta
          name="description"
          content={content.metaDescription || "Contact our support and hiring team."}
        />
      </Helmet>

      {loading ? (
        <section className="portal-loader-shell">
          <div className="portal-container">
            <div className="portal-loader-card">
              <div className="portal-round-loader" role="status" aria-label="Loading contact page"></div>
              <h2 className="h5 fw-bold mb-2">Loading contact page...</h2>
              <p className="text-muted mb-0">Preparing support and contact information.</p>
            </div>
          </div>
        </section>
      ) : (
        <section className="portal-section">
          <div className="portal-container">
            <div className="row justify-content-center gy-4">
              <div className="col-lg-7">
                <div className="portal-card p-3 p-md-4">
                  <span className="portal-kicker">Contact Support</span>
                  <h1 className="portal-title mb-3">{content.title}</h1>
                  <div
                    className="portal-muted leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: content.description || "" }}
                  />
                </div>

                <div className="portal-card p-3 p-md-4 mt-3">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="portal-contact-feature">
                        <div className="portal-contact-icon">24/7</div>
                        <h2 className="h6 fw-bold mb-2">Fast Support</h2>
                        <p className="portal-muted small mb-0">
                          Reach out for hiring help, recruiter onboarding, or candidate support.
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="portal-contact-feature">
                        <div className="portal-contact-icon">HR</div>
                        <h2 className="h6 fw-bold mb-2">Recruitment Guidance</h2>
                        <p className="portal-muted small mb-0">
                          Our team can guide employers and job seekers through the platform.
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="portal-contact-feature">
                        <div className="portal-contact-icon">ETA</div>
                        <h2 className="h6 fw-bold mb-2">Quick Response</h2>
                        <p className="portal-muted small mb-0">
                          Most enquiries are reviewed promptly during working hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <aside className="col-lg-5">
                <div className="portal-card p-3 p-md-4 mb-3">
                  <h2 className="h6 fw-bold mb-3">Contact Details</h2>
                  <div className="d-grid gap-2 small portal-muted">
                    <div>Email: {homeData?.email || "support@ynbjobs.com"}</div>
                    <div>Phone: {homeData?.phone || "+91 00000 00000"}</div>
                    <div>Address: {homeData?.address || "India"}</div>
                  </div>
                </div>

                <div className="portal-card p-3 p-md-4">
                  <h2 className="h6 fw-bold mb-3">Send a Message</h2>
                  <form onSubmit={submitContact}>
                    <div className="mb-3">
                      <label className="small fw-bold portal-muted mb-1 d-block">Full Name</label>
                      <input
                        className="portal-input"
                        value={formData.name}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, name: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="small fw-bold portal-muted mb-1 d-block">Email Address</label>
                      <input
                        type="email"
                        className="portal-input"
                        value={formData.email}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, email: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="small fw-bold portal-muted mb-1 d-block">How can we help?</label>
                      <textarea
                        className="portal-textarea"
                        rows="5"
                        value={formData.message}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, message: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <button className="portal-btn-primary w-100" disabled={submitting}>
                      {submitting ? "Sending..." : "Submit Inquiry"}
                    </button>
                  </form>
                </div>
              </aside>
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
};

export default PublicContactPage;
